import { useState } from 'react';
import { Modal, ModalFooter } from './Modal';
import { Button } from '../ui/Button';
import { apiKeyApi, ApiKey } from '../../services/api';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (newKey: ApiKey) => void;
}

type Step = 'form' | 'reveal';

export function GenerateKeyModal({ isOpen, onClose, onSuccess }: Props) {
  const [step, setStep] = useState<Step>('form');
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generatedKey, setGeneratedKey] = useState('');
  const [copied, setCopied] = useState(false);
  const [createdKey, setCreatedKey] = useState<ApiKey | null>(null);

  function handleClose() {
    if (step === 'reveal' && createdKey) {
      onSuccess(createdKey);
    }
    setStep('form');
    setName('');
    setNameError('');
    setGeneratedKey('');
    setCopied(false);
    setCreatedKey(null);
    onClose();
  }

  async function handleGenerate() {
    if (!name.trim()) {
      setNameError('Key name is required');
      return;
    }
    setNameError('');
    setIsSubmitting(true);
    try {
      const result = await apiKeyApi.create({ name: name.trim() });
      if (result) {
        setGeneratedKey(result.key);
        setCreatedKey(result.apiKey);
        // Store in sessionStorage so user can re-copy within this browser session
        sessionStorage.setItem(`api_key_${result.apiKey.id}`, result.key);
        setStep('reveal');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to generate key';
      setNameError(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleCopy() {
    await navigator.clipboard.writeText(generatedKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={step === 'form' ? 'Generate API Key' : 'Your New API Key'}
      size="medium"
    >
      {step === 'form' ? (
        <div className="space-y-4">
          <p className="text-sm text-text-secondary">
            Give this key a name so you can identify it later (e.g. "Zapier integration").
          </p>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Key name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !isSubmitting && handleGenerate()}
              placeholder="e.g. Zapier integration"
              maxLength={100}
              autoFocus
              className="w-full px-3 py-2 rounded-md border border-border-default bg-surface-default text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {nameError && (
              <p className="mt-1 text-sm text-red-500">{nameError}</p>
            )}
          </div>
          <ModalFooter>
            <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleGenerate} disabled={isSubmitting}>
              {isSubmitting ? 'Generating…' : 'Generate key'}
            </Button>
          </ModalFooter>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
            <p className="text-sm font-medium text-amber-800">
              Store this key — it won't be shown again.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              API key
            </label>
            <div className="flex gap-2">
              <code className="flex-1 px-3 py-2 rounded-md border border-border-default bg-background-secondary text-text-primary text-sm font-mono break-all">
                {generatedKey}
              </code>
              <Button variant="secondary" size="sm" onClick={handleCopy}>
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
          </div>
          <ModalFooter>
            <Button variant="primary" onClick={handleClose}>
              Done
            </Button>
          </ModalFooter>
        </div>
      )}
    </Modal>
  );
}
